import { useCallback } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useLoginOrSignupPageState } from '#src/app/pages/loginOrSignup/pageContext';

import { useLoginOrSignup } from '../../../useLoginOrSignup';

import { useAuthMethodPerimeterXCaptcha } from './useAuthMethodPerimeterXCaptcha';

type GotoLoginOrSignupHook = (email?: string) => Promise<void>;

export function useGotoLoginOrSignup(): GotoLoginOrSignupHook {
	const [state, { setLoading, setSuccess, setError, gotoStep, setAuthMethod }] = useLoginOrSignupPageState();
	const { getEmailAuthMethod } = useLoginOrSignup();
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();
	const triggerPerimeterXCaptcha = useAuthMethodPerimeterXCaptcha();
	const gotoLoginOrSignup = useCallback(
		async (email?: string) => {
			const emailToUse = email ?? state.email;
			setLoading();
			async function attempt() {
				try {
					const authMethod = await getEmailAuthMethod(emailToUse, utilityContext);
					setSuccess();
					setAuthMethod(authMethod);
					if (authMethod === 'INVALID_EMAIL') {
						// TODO update error with a clearer message explaining what could be wrong with the email address
						setError(translate('fe.errors.user.email.prefix'));
					} else if (authMethod === 'UNKNOWN_USER') {
						gotoStep('signup', { canGoBack: true });
					} else {
						gotoStep('otp', { canGoBack: true });
					}
				} catch (error: unknown) {
					if (error instanceof GQLResponsePerimeterXChallengeError) {
						await triggerPerimeterXCaptcha({
							pxResponse: error.pxChallenge,
						});
						await attempt();
					} else {
						const loginError = typeof error === 'string' ? error : translate('corgi.login_or_signup.error.network');
						setError(loginError);
					}
				}
			}
			await attempt();
		},
		[
			setLoading,
			setSuccess,
			setError,
			gotoStep,
			setAuthMethod,
			getEmailAuthMethod,
			state.email,
			utilityContext,
			translate,
			triggerPerimeterXCaptcha,
		],
	);

	return gotoLoginOrSignup;
}
